import { getConsolidatedValue } from "utils/getConsolidatedValue"

import { type ConsolidatedDeliveryTerms } from "../useDeliveryTermsPerRequestedDate"

import {
  OrderCreateInputSchema,
  OrderStatus,
  OrderType,
  type PricingPackage,
  type Product,
} from "@/graphql/codegen/graphql"
import type { ProductSelectionItem, PurchaseOrderFormData } from "@/screens/Procurement/Supplier/types/types.ts"
import { getPOLocationAssociationId } from "@/screens/Procurement/utils/locationUtils"

export const getLocationIdPerProductList = (products: Product[] | ProductSelectionItem[]): string | null => {
  const productWithLocations = products.find(
    ({ locationsAssociations }) => Array.isArray(locationsAssociations) && locationsAssociations.length > 0
  )

  return productWithLocations?.locationsAssociations?.[0]?.locationAssociationId ?? null
}

export const getCompanyIdForProductList = (products: Product[] | ProductSelectionItem[]): string | null => {
  return products?.[0]?.companyId ?? products?.[0]?.company?.companyId ?? null
}

export const extractConsolidatedDeliveryTerms = (pricingPackages: PricingPackage[]): ConsolidatedDeliveryTerms => {
  return {
    freightTermsConsolidated: getConsolidatedValue(pricingPackages, "freightTerms"),
    incotermsConsolidated: getConsolidatedValue(pricingPackages, "incoterms"),
    incotermsLocationIdConsolidated: getConsolidatedValue(pricingPackages, "incotermsLocationId"),
  }
}

export const createPOFormData = (
  data: PurchaseOrderFormData,
  selectedProducts: ProductSelectionItem[],
  requestedDeliveryDate: string | null
): ReturnType<ReturnType<typeof OrderCreateInputSchema>["safeParse"]> => {
  const sourceLocationId = getPOLocationAssociationId(selectedProducts)

  const draftPO = OrderCreateInputSchema().safeParse({
    destinationLocationAssociationId: data.shipTo.locationAssociationId,
    warehouseAreaId: data.shipTo.warehouseAreaId,
    orderLines: selectedProducts.map((product) => {
      const defaultUoM = product.packageUsWeightUom

      return {
        numberOfPallets: 0,
        pricePerUom: 0,
        priceUomId: defaultUoM,
        productId: product.productId,
        productVersionId: product.versionId,
        quantityInUom: 0,
        uomId: defaultUoM,
      }
    }),
    freightTerms: data.freightTerms ?? null,
    deliveryTerms: data.deliveryTerms ?? null,
    shipViaCompanyId: data.shipViaCompanyId,
    deliveryLocationId: data.deliveryLocationId ?? null,
    status: OrderStatus.Draft,
    type: OrderType.PurchaseOrder,
    requestedDeliveryDate,
    sourceLocationAssociationId: sourceLocationId,
  })

  return draftPO
}
